import React, { useMemo, useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBook,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faSearch,
  faRobot,
  faSpinner,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { aiChatApi } from '../api/aiChatApi';

/** Desk + Alpaca APIs available inside algorithm scripts. */
export const ALGO_API_CATALOG = [
  {
    group: 'Globals (injected)',
    items: [
      {
        name: 'symbol',
        sig: 'string',
        desc: 'Primary ticker from the IDE Symbols field. Use this instead of hardcoding const symbol = "AAPL".',
        params: [],
        returns: 'e.g. "AAPL"',
      },
      {
        name: 'symbols',
        sig: 'string[]',
        desc: 'All tickers from the IDE Symbols field (comma/space separated). Empty array if the field is blank (earnings scan mode).',
        params: [],
        returns: '["AAPL","MSFT"]',
      },
      {
        name: 'symbolY',
        sig: 'string',
        desc: 'First ticker — pairs dependent leg (Y in Y − βX). Same as symbol when only one name is set.',
        params: [],
      },
      {
        name: 'symbolX',
        sig: 'string',
        desc: 'Second ticker — pairs hedge leg. Empty string if only one symbol is set.',
        params: [],
      },
      {
        name: 'alpaca',
        sig: 'object',
        desc: 'Venue execution client (Alpaca paper/live, or Schwab-mapped). Orders, account, positions, market data.',
        params: [],
      },
      {
        name: 'helpers',
        sig: 'object',
        desc: 'Desk helpers: clock, bars, sizing, brackets, TWAP, ownership, schedule. Mirrors symbol/symbols from the IDE.',
        params: [],
      },
      {
        name: 'console',
        sig: 'object',
        desc: 'Logs to the Output tab. Only log / warn / error are captured — return values are not printed unless you log them.',
        params: [
          { name: '...args', type: 'any', desc: 'Values are JSON-stringified if they are objects.' },
        ],
      },
    ],
  },
  {
    group: 'helpers — session & state',
    items: [
      {
        name: 'helpers.symbol / .symbols / .symbolY / .symbolX',
        sig: 'same as globals',
        desc: 'Same tickers as the injected globals, also on helpers.',
        params: [],
      },
      {
        name: 'helpers.tradeOnlyWhenOpen',
        sig: 'boolean',
        desc: 'True when the “Trade only when market open” checkbox is on. Scripts should skip entries when this is true and the clock is closed.',
        params: [],
      },
      {
        name: 'helpers.manageExistingPositions',
        sig: 'boolean',
        desc: 'When true, exit logic may act on holdings you already owned before Start. Also used by Kill + close tracking after adopt.',
        params: [],
      },
      {
        name: 'helpers.venue / .env / .accountNumber',
        sig: 'string',
        desc: 'Execution venue (alpaca|schwab), paper|live, and Schwab account number when applicable.',
        params: [],
      },
      {
        name: 'helpers.schedule',
        sig: 'object',
        desc: 'Normalized run schedule (tradeOnlyWhenOpen, skip windows, custom hours, dates).',
        params: [],
      },
      {
        name: 'helpers.state',
        sig: 'object',
        desc: 'Mutable bag that survives each interval tick until Stop. Use for day counters, last signal, etc. Cleared when the algo stops.',
        params: [],
      },
      {
        name: 'helpers.isBacktest',
        sig: 'boolean | undefined',
        desc: 'True only inside the backtester. Undefined/falsy on live ticks. Skip helpers.startTwap and getEarningsCalendar when this is true — they are not historical.',
        params: [],
      },
      {
        name: 'helpers.ensureTradingSession',
        sig: 'ensureTradingSession(clock?, message?) → Promise<boolean>',
        desc: 'Returns false (and logs) when tradeOnlyWhenOpen is on and the session is closed. Pass an existing clock to avoid a second fetch.',
        params: [
          { name: 'clock', type: 'object|null', desc: 'Optional clock from getClock(). Fetched if omitted.' },
          { name: 'message', type: 'string|null', desc: 'Override skip log line.' },
        ],
        returns: 'true if trading is allowed this tick',
      },
      {
        name: 'helpers.isInSchedule',
        sig: 'isInSchedule() → Promise<{ ok, reason, clock }>',
        desc: 'Full schedule gate (hours, dates, open/close skip). The server already skips the tick when ok is false; this is for custom branching.',
        params: [],
      },
      {
        name: 'helpers.getClock',
        sig: 'getClock() → Promise<clock>',
        desc: 'US equity session clock. Use is_open or isOpen (both set). Also next_open, next_close.',
        params: [],
        returns: '{ is_open, isOpen, next_open, next_close, timestamp }',
      },
      {
        name: 'helpers.assertMarketOpen',
        sig: 'assertMarketOpen() → Promise<clock>',
        desc: 'Throws only if tradeOnlyWhenOpen is on and the market is closed. Otherwise returns the clock.',
        params: [],
      },
    ],
  },
  {
    group: 'helpers — market data',
    items: [
      {
        name: 'helpers.getBars',
        sig: 'getBars(symbol, options?) → Promise<bar[]>',
        desc: 'Daily/intraday bars (IEX + fallbacks). Prefer this over raw getBarsV2 when you want a flat array.',
        params: [
          { name: 'symbol', type: 'string', desc: 'Ticker, e.g. symbol or "AAPL".' },
          { name: 'options.timeframe', type: 'string', desc: '1Day | 1Hour | 15Min | 5Min | 1Min. Default 1Day.' },
          { name: 'options.limit', type: 'number', desc: 'Max bars. Default 120.' },
          { name: 'options.start / end', type: 'string|null', desc: 'ISO dates. Optional; otherwise inferred.' },
        ],
        returns: '[{ ClosePrice|c, OpenPrice|o, HighPrice|h, LowPrice|l, Volume|v, Timestamp|t }]',
      },
      {
        name: 'helpers.getLatestBar',
        sig: 'getLatestBar(symbol, options?) → Promise<bar|null>',
        desc: 'Last completed bar from getBars (limit 1 unless you pass a larger limit).',
        params: [
          { name: 'symbol', type: 'string', desc: 'Ticker.' },
          { name: 'options.timeframe', type: 'string', desc: 'Default 1Day.' },
        ],
      },
      {
        name: 'helpers.getLatestTrade',
        sig: 'getLatestTrade(symbol) → Promise<trade>',
        desc: 'Last print. Price may be Price, p, or price depending on feed.',
        params: [{ name: 'symbol', type: 'string', desc: 'Ticker.' }],
      },
      {
        name: 'helpers.getLatestQuote',
        sig: 'getLatestQuote(symbol) → Promise<quote>',
        desc: 'NBBO-style quote. AskPrice/ap, BidPrice/bp.',
        params: [{ name: 'symbol', type: 'string', desc: 'Ticker.' }],
      },
      {
        name: 'helpers.getAccount',
        sig: 'getAccount() → Promise<account>',
        desc: 'Paper/live (or Schwab) account. In backtest this is the simulated cash/equity.',
        params: [],
        returns: '{ cash, equity, buying_power, portfolio_value, status, … }',
      },
      {
        name: 'helpers.getMarkovRegime',
        sig: "getMarkovRegime(symbol, { timeframe='1Day', limit=500, window=20, threshold=0.05, forecastSteps=1 }) → Promise<regime>",
        desc: 'Markov Bull/Bear/Sideways from rolling returns + transition matrix. Signal = bull_prob − bear_prob in [-1,1]. Use as a standalone bias or to gate another strategy. Observable model only (no HMM). Framework: Roan (@RohOnChain) / markov-hedge-fund-method.',
        params: [
          { name: 'symbol', type: 'string', desc: 'Ticker (defaults to IDE primary).' },
          { name: 'window', type: 'number', desc: 'Rolling-return lookback bars (default 20).' },
          { name: 'threshold', type: 'number', desc: '± return for Bull/Bear vs Sideways (default 0.05).' },
          { name: 'forecastSteps', type: 'number', desc: 'Raise transition matrix to this power for n-step probs (default 1).' },
        ],
        returns: '{ ok, currentRegime, signal, bias, nextStateProbabilities, transitionMatrix, persistenceDiagonal, stationaryDistribution, … }',
      },
      {
        name: 'helpers.getEarningsCalendar',
        sig: 'getEarningsCalendar({ day|start|end|days, size }) → Promise<result>',
        desc: 'Yahoo earnings calendar (same source as the dashboard). Not historical in backtest.',
        params: [
          { name: 'day', type: 'string', desc: 'YYYY-MM-DD for a single day.' },
          { name: 'start / end', type: 'string', desc: 'Inclusive date range.' },
          { name: 'days', type: 'number', desc: 'Lookahead from today if start/end omitted.' },
          { name: 'size', type: 'number', desc: 'Max events to return.' },
        ],
      },
      {
        name: 'helpers.getNews',
        sig: 'getNews(symbol, { limit }) → Promise<{ headline, source, sentiment, publishedAt }[]>',
        desc: 'Recent desk news headlines for a ticker. Empty array if the news table has nothing. Not historical in backtest.',
        params: [
          { name: 'symbol', type: 'string', desc: 'Ticker.' },
          { name: 'limit', type: 'number', desc: 'Default 5, max 20.' },
        ],
      },
    ],
  },
  {
    group: 'helpers — AI (Ollama)',
    items: [
      {
        name: 'helpers.ollamaModel',
        sig: 'string',
        desc: 'Default local model from OLLAMA_MODEL (often llama3.2). Override per call with askModel({ model }).',
        params: [],
      },
      {
        name: 'helpers.askModel',
        sig: 'askModel({ system, user, model, schema, temperature }) → Promise<{ ok, parsed, error, model }>',
        desc: 'Calls local Ollama and parses JSON. Default schema is a trade decision. Invalid JSON / unknown tickers become hold. Not run inside backtest (returns hold). Tick timeout is 90s; keep Interval well above model latency.',
        params: [
          { name: 'user', type: 'string|object', desc: 'Prompt or snapshot object (JSON-stringified).' },
          { name: 'system', type: 'string', desc: 'Optional system instructions.' },
          { name: 'model', type: 'string', desc: 'Ollama tag. Default helpers.ollamaModel.' },
          { name: 'schema', type: 'object|false', desc: 'JSON schema (default trade decision). false = raw json object, no normalize.' },
          { name: 'temperature', type: 'number', desc: 'Default 0.2 (low = more consistent JSON).' },
          { name: 'allowedSymbols', type: 'string[]', desc: 'Override universe; default is the IDE Symbols list.' },
        ],
        returns: '{ ok, parsed: { action, symbol, qty, confidence, reason }, error }',
      },
      {
        name: 'parsed.action',
        sig: "'hold'|'buy'|'sell'|'flatten'",
        desc: 'AI Desk Agent actions. hold = skip; buy = long; sell = short or reduce; flatten = close the position. Load the AI Desk Agent template for the full paper-first runner.',
        params: [
          { name: 'confidence', type: 'number', desc: '0–1. Script should hold below minConfidence (template default 0.65).' },
          { name: 'qty', type: 'number|null', desc: 'Null → size with sizeByRiskPct.' },
          { name: 'allowLiveOrders', type: 'boolean', desc: 'Template flag. Live header + true required to send live orders; otherwise it logs WOULD …' },
        ],
      },
    ],
  },
  {
    group: 'helpers — orders & risk',
    items: [
      {
        name: 'helpers.sizeByRiskPct',
        sig: 'sizeByRiskPct(riskPct, price) → Promise<number>',
        desc: 'Share qty as % of equity, capped by algo budget / max notional / max qty when risk limits are on. Backtest also caps by simulated cash.',
        params: [
          { name: 'riskPct', type: 'number', desc: 'Percent of equity. Default 1.' },
          { name: 'price', type: 'number', desc: 'Reference price (usually last close).' },
        ],
        returns: 'integer qty (≥ 0 in backtest, ≥ 1 live if equity allows)',
      },
      {
        name: 'helpers.createBracketOrder',
        sig: 'createBracketOrder({ … }) → Promise<order>',
        desc: 'Parent order plus take-profit limit and stop-loss. Percentages need a last trade/quote if prices are omitted.',
        params: [
          { name: 'symbol', type: 'string', desc: 'Ticker.' },
          { name: 'qty', type: 'number', desc: 'Shares.' },
          { name: 'side', type: "'buy'|'sell'", desc: 'Default buy.' },
          { name: 'type', type: 'string', desc: 'market | limit. Default market.' },
          { name: 'limitPrice', type: 'number|null', desc: 'Required for limit entries.' },
          { name: 'takeProfitPct', type: 'number', desc: 'Default 2. Ignored if takeProfitPrice set.' },
          { name: 'stopLossPct', type: 'number', desc: 'Default 1. Ignored if stopLossPrice set.' },
          { name: 'takeProfitPrice / stopLossPrice', type: 'number|null', desc: 'Absolute prices.' },
          { name: 'timeInForce', type: 'string', desc: 'Default gtc.' },
        ],
      },
      {
        name: 'helpers.createTrailingStop',
        sig: 'createTrailingStop({ symbol, qty, trailPercent|trailPrice })',
        desc: 'Trailing stop order. Need trailPercent or trailPrice.',
        params: [
          { name: 'symbol', type: 'string', desc: 'Ticker.' },
          { name: 'qty', type: 'number', desc: 'Shares (usually the long qty).' },
          { name: 'side', type: 'string', desc: 'Default sell.' },
          { name: 'trailPercent', type: 'number|null', desc: 'e.g. 2 for 2%.' },
          { name: 'trailPrice', type: 'number|null', desc: 'Dollar trail instead of percent.' },
          { name: 'timeInForce', type: 'string', desc: 'Default gtc.' },
        ],
      },
      {
        name: 'helpers.createAlgoOrder',
        sig: 'createAlgoOrder({ symbol, qty, side, algorithm, … })',
        desc: 'Alpaca Elite Smart Router VWAP/TWAP. Paper accepts the field but may not simulate those fills.',
        params: [
          { name: 'symbol', type: 'string', desc: 'Ticker.' },
          { name: 'qty', type: 'number', desc: 'Shares.' },
          { name: 'side', type: "'buy'|'sell'", desc: 'Required.' },
          { name: 'type', type: 'string', desc: 'limit (default) or market.' },
          { name: 'algorithm', type: 'string', desc: 'TWAP | VWAP. Default TWAP.' },
          { name: 'limitPrice', type: 'number', desc: 'For limit-type algo orders.' },
          { name: 'timeInForce', type: 'string', desc: 'Default day.' },
          { name: 'startTime / endTime', type: 'string', desc: 'ISO window.' },
          { name: 'maxPercentage', type: 'number', desc: 'Participation cap.' },
        ],
      },
      {
        name: 'helpers.startTwap',
        sig: 'startTwap(params) → Promise<job>',
        desc: 'Server-side equal-slice TWAP (same engine as the TWAP tab). Not simulated in backtest. Respects the market-open setting unless you pass tradeOnlyWhenOpen: false.',
        params: [
          { name: 'symbol', type: 'string', desc: 'Ticker to work.' },
          { name: 'side', type: "'buy'|'sell'", desc: 'Default buy.' },
          { name: 'qty', type: 'number', desc: 'Total shares across all slices.' },
          { name: 'durationMinutes', type: 'number', desc: 'Window length. Default 30. Interval = duration / slices.' },
          { name: 'slices', type: 'number', desc: 'Child orders, 1–100. Default 10. Last slice takes the remainder.' },
          { name: 'limitOffset', type: 'number|null', desc: 'If set, each slice is a limit at last print ± offset. Omit for market slices.' },
          { name: 'tradeOnlyWhenOpen', type: 'boolean', desc: 'Override the IDE checkbox for this job only.' },
        ],
        returns: '{ id, symbol, side, qty, slices, … } — pass id to stopTwap',
      },
      {
        name: 'helpers.stopTwap',
        sig: 'stopTwap(id) → boolean',
        desc: 'Cancel remaining TWAP slices. Already-filled slices stay filled.',
        params: [{ name: 'id', type: 'string', desc: 'Job id from startTwap (or the TWAP panel).' }],
      },
      {
        name: 'helpers.emitSignal',
        sig: 'emitSignal({ kind, label, symbol, price, side, meta })',
        desc: 'Pushes a marker onto the Chart tab / event feed. Does not place an order.',
        params: [
          { name: 'kind', type: 'string', desc: 'e.g. golden_cross, order, exit.' },
          { name: 'label / message', type: 'string', desc: 'Shown on the chart.' },
          { name: 'symbol / price / side', type: 'optional', desc: 'For overlays.' },
        ],
      },
    ],
  },
  {
    group: 'helpers — ownership',
    items: [
      {
        name: 'helpers.adoptExistingPositions',
        sig: 'adoptExistingPositions(symbols?) → Promise<adopted[]>',
        desc: 'Marks open account positions as owned by this algo so exits and Kill + close can track them. Call when manageExistingPositions is on.',
        params: [
          { name: 'symbols', type: 'string[]|null', desc: 'Filter; null = all positions.' },
        ],
      },
      {
        name: 'helpers.getAlgoOwnedQty',
        sig: 'getAlgoOwnedQty(symbol) → number',
        desc: 'Signed qty this runner attributes to algorithms (not the whole account).',
        params: [{ name: 'symbol', type: 'string', desc: 'Ticker.' }],
      },
      {
        name: 'helpers.isAlgoOwned',
        sig: 'isAlgoOwned(symbol) → boolean',
        desc: 'True if |owned qty| > 0 for this algo.',
        params: [{ name: 'symbol', type: 'string', desc: 'Ticker.' }],
      },
      {
        name: 'helpers.getAlgoOwnedPositions',
        sig: 'getAlgoOwnedPositions() → { [symbol]: qty }',
        desc: 'Map of symbols this runner currently tracks.',
        params: [],
      },
    ],
  },
  {
    group: 'alpaca — market data',
    items: [
      {
        name: 'alpaca.getBarsV2',
        sig: 'getBarsV2(symbols, options) → Promise<{ [sym]: bar[] }>',
        desc: 'Packed bars by symbol. Pass an array of tickers. Timeframe/limit/start/end like getBars. Use barsResponse[symbol] for the series.',
        params: [
          { name: 'symbols', type: 'string|string[]', desc: 'One ticker or a list.' },
          { name: 'options.timeframe', type: 'string', desc: '1Day, 5Min, …' },
          { name: 'options.limit', type: 'number', desc: 'Max bars per symbol.' },
          { name: 'options.start / end', type: 'string|null', desc: 'ISO range.' },
        ],
      },
      {
        name: 'alpaca.getLatestTrade',
        sig: 'getLatestTrade(symbol) → Promise<trade>',
        desc: 'Last print. Prefer Price ?? p ?? price. IEX/Alpaca feed even when venue is Schwab.',
        params: [{ name: 'symbol', type: 'string', desc: 'Ticker.' }],
      },
      {
        name: 'alpaca.getLatestQuote',
        sig: 'getLatestQuote(symbol) → Promise<quote>',
        desc: 'NBBO-style quote. AskPrice/ap, BidPrice/bp. Same market-data feed as getLatestTrade.',
        params: [{ name: 'symbol', type: 'string', desc: 'Ticker.' }],
      },
      {
        name: 'alpaca.getSnapshot',
        sig: 'getSnapshot(symbol) → Promise<snapshot>',
        desc: 'Alpaca snapshot (latest trade, quote, minute bar). Not implemented on the Schwab adapter — use getLatestTrade/Quote/getBarsV2 instead.',
        params: [{ name: 'symbol', type: 'string', desc: 'Ticker.' }],
      },
      {
        name: 'alpaca.getClock',
        sig: 'getClock() → Promise<clock>',
        desc: 'Alpaca session clock. Prefer helpers.getClock() which normalizes is_open and isOpen.',
        params: [],
      },
      {
        name: 'alpaca.getAccount',
        sig: 'getAccount() → Promise<account>',
        desc: 'Same as helpers.getAccount(). Always console.log the result if you want to inspect fields.',
        params: [],
      },
    ],
  },
  {
    group: 'alpaca — orders',
    items: [
      {
        name: 'alpaca.createOrder',
        sig: 'createOrder(order) → Promise<order>',
        desc: 'Submit an order. Goes through desk risk limits when enabled (qty, notional, daily loss). Logged as createOrder → {…}.',
        params: [
          { name: 'symbol', type: 'string', desc: 'Ticker (uppercase recommended).' },
          { name: 'qty', type: 'number|string', desc: 'Share quantity. Use notional instead for dollar orders.' },
          { name: 'notional', type: 'number|string', desc: 'Dollar amount (fractional when supported).' },
          { name: 'side', type: "'buy'|'sell'", desc: 'Required.' },
          { name: 'type', type: 'string', desc: 'market | limit | stop | stop_limit | trailing_stop.' },
          { name: 'time_in_force', type: 'string', desc: 'day | gtc | ioc | fok | opg | cls.' },
          { name: 'limit_price', type: 'number', desc: 'For limit / stop_limit.' },
          { name: 'stop_price', type: 'number', desc: 'For stop / stop_limit.' },
          { name: 'order_class', type: 'string', desc: 'simple | bracket | oto | oco.' },
          { name: 'take_profit', type: '{ limit_price }', desc: 'Bracket take-profit.' },
          { name: 'stop_loss', type: '{ stop_price, limit_price? }', desc: 'Bracket stop.' },
          { name: 'extended_hours', type: 'boolean', desc: 'Limit orders outside RTH when the broker allows.' },
          { name: 'client_order_id', type: 'string', desc: 'Optional idempotency key (Alpaca).' },
          { name: 'trail_percent / trail_price', type: 'number', desc: 'For type trailing_stop. Or use helpers.createTrailingStop.' },
        ],
      },
      {
        name: 'alpaca.getAllOrders / getOrders',
        sig: 'getAllOrders(options?) / getOrders(options?) → Promise<order[]>',
        desc: 'Open/historical orders. Alpaca SDK uses getOrders / getAllOrders; the Schwab adapter exposes getOrders only.',
        params: [
          { name: 'status', type: 'string', desc: 'open | closed | all. Default open on Schwab.' },
          { name: 'limit', type: 'number', desc: 'Page size.' },
          { name: 'symbols', type: 'string[]', desc: 'Optional Alpaca filter.' },
        ],
      },
      {
        name: 'alpaca.getOrder',
        sig: 'getOrder(orderId) → Promise<order>',
        desc: 'Fetch one order by id (Alpaca). Not on the Schwab adapter.',
        params: [{ name: 'orderId', type: 'string', desc: 'Broker order id.' }],
      },
      {
        name: 'alpaca.cancelOrder',
        sig: 'cancelOrder(orderId) → Promise<…>',
        desc: 'Cancel one open order. Kill algos also cancels tracked-symbol orders.',
        params: [{ name: 'orderId', type: 'string', desc: 'Broker order id.' }],
      },
      {
        name: 'alpaca.cancelAllOrders',
        sig: 'cancelAllOrders() → Promise<…>',
        desc: 'Cancel every open order on the account (Alpaca). Prefer cancelOrder or Kill algos so you do not wipe unrelated working orders. Not on the Schwab adapter.',
        params: [],
      },
      {
        name: 'alpaca.replaceOrder',
        sig: 'replaceOrder(orderId, patch) → Promise<order>',
        desc: 'Modify qty/price of a working Alpaca order. Not on the Schwab adapter.',
        params: [
          { name: 'orderId', type: 'string', desc: 'Existing order.' },
          { name: 'qty / limit_price / stop_price / time_in_force', type: 'optional', desc: 'Fields to replace.' },
        ],
      },
    ],
  },
  {
    group: 'alpaca — positions',
    items: [
      {
        name: 'alpaca.getAllPositions / getPositions',
        sig: 'getAllPositions() / getPositions() → Promise<position[]>',
        desc: 'All open positions on the execution account (not just algo-owned). Alpaca SDK: getPositions / getAllPositions. Schwab adapter: getPositions only.',
        params: [],
        returns: '[{ symbol, qty, avg_entry_price, market_value, unrealized_pl, … }]',
      },
      {
        name: 'alpaca.getPosition',
        sig: 'getPosition(symbol) → Promise<position>',
        desc: 'Throws if you have no position. Wrap in try/catch and treat as qty 0.',
        params: [{ name: 'symbol', type: 'string', desc: 'Ticker.' }],
      },
      {
        name: 'alpaca.closePosition',
        sig: 'closePosition(symbol) → Promise<order>',
        desc: 'Market flatten that symbol. Untracks algo ownership for Kill.',
        params: [{ name: 'symbol', type: 'string', desc: 'Ticker.' }],
      },
      {
        name: 'alpaca.closeAllPositions',
        sig: 'closeAllPositions() → Promise<…>',
        desc: 'Flattens the whole account — rarely what you want from a script. Prefer closePosition(symbol) or Kill + close. Not on the Schwab adapter.',
        params: [],
      },
    ],
  },
  {
    group: 'helpers — backtest only',
    items: [
      {
        name: 'helpers.backtestSymbol',
        sig: 'string',
        desc: 'Primary symbol the backtester is walking. Same as helpers.symbol / the IDE Symbols field.',
        params: [],
      },
      {
        name: 'helpers.initialCash',
        sig: 'number',
        desc: 'Starting simulated cash from the Backtest panel. Seeded into getAccount().cash / equity at bar 0.',
        params: [],
      },
      {
        name: 'helpers.availableCash / helpers.equity',
        sig: 'number (getters)',
        desc: 'Live simulated cash and mark-to-market equity as of the current bar. sizeByRiskPct uses these.',
        params: [],
      },
      {
        name: 'helpers.currentBar',
        sig: 'bar | null',
        desc: 'The bar the engine is currently evaluating (no lookahead). Use ClosePrice / HighPrice / LowPrice / Timestamp.',
        params: [],
      },
      {
        name: 'helpers.barIndex',
        sig: 'number',
        desc: '0-based index of currentBar in the primary series.',
        params: [],
      },
    ],
  },
  {
    group: 'Data shapes',
    items: [
      {
        name: 'bar',
        sig: '{ Timestamp|t, OpenPrice|o, HighPrice|h, LowPrice|l, ClosePrice|c, Volume|v, VWAP, TradeCount }',
        desc: 'Both long Alpaca-style names and short ohlcv keys are filled by getBars / getBarsV2 so templates can use either.',
        params: [
          { name: 'ClosePrice / c', type: 'number', desc: 'Session or bar close. Use this for SMA/RSI.' },
          { name: 'Timestamp / t', type: 'string', desc: 'ISO time of the bar.' },
          { name: 'Volume / v', type: 'number', desc: 'Share volume.' },
        ],
      },
      {
        name: 'account',
        sig: '{ cash, equity, buying_power, portfolio_value, status, … }',
        desc: 'Numeric fields are often strings from the broker. Always Number(...) them. Schwab maps cash/equity/buyingPower into this shape.',
        params: [
          { name: 'cash', type: 'string|number', desc: 'Settled cash.' },
          { name: 'equity / portfolio_value', type: 'string|number', desc: 'Account equity.' },
          { name: 'buying_power', type: 'string|number', desc: 'Buying power (day-trade BP on PDT accounts).' },
        ],
      },
      {
        name: 'position',
        sig: '{ symbol, qty, avg_entry_price, market_value, unrealized_pl, current_price, … }',
        desc: 'qty is signed (negative = short) and often a string. getPosition throws if you are flat — catch and treat as 0.',
        params: [
          { name: 'qty', type: 'string|number', desc: 'Signed share count.' },
          { name: 'avg_entry_price', type: 'string|number', desc: 'Average fill of the open position.' },
          { name: 'unrealized_pl', type: 'string|number', desc: 'Open P/L in dollars.' },
        ],
      },
      {
        name: 'clock',
        sig: '{ is_open, isOpen, next_open, next_close, timestamp }',
        desc: 'US equity session. helpers.getClock sets both is_open and isOpen. next_* are ISO timestamps.',
        params: [],
      },
      {
        name: 'order (createOrder result)',
        sig: '{ id, status, symbol, qty, side, type, filled_avg_price, … }',
        desc: 'Broker acknowledgement. Paper fills immediately for market orders; live/Schwab may stay accepted/working. Always log the id.',
        params: [],
      },
    ],
  },
  {
    group: 'console',
    items: [
      {
        name: 'console.log',
        sig: 'console.log(...args)',
        desc: 'Shown in the Output tab as info. Objects are JSON-stringified. This is the only way to inspect return values.',
        params: [{ name: '...args', type: 'any', desc: 'Any serializable values.' }],
      },
      {
        name: 'console.warn / console.error',
        sig: 'console.warn(...args) / console.error(...args)',
        desc: 'Same capture as log, tagged warn/error in Output.',
        params: [],
      },
    ],
  },
  {
    group: 'Venue notes (Alpaca vs Schwab)',
    items: [
      {
        name: 'Schwab adapter limits',
        sig: 'venue === "schwab"',
        desc: 'createOrder supports simple market/limit/stop/stop_limit only. Brackets, OCO, trailing_stop, cancelAllOrders, closeAllPositions, getSnapshot, and replaceOrder are Alpaca-only. Market data still uses IEX/Alpaca bars.',
        params: [],
      },
      {
        name: 'Risk gates',
        sig: 'Risk settings (Settings page)',
        desc: 'When enabled, createOrder blocks buys that exceed max qty, algo budget / max notional, daily loss, or algo max-loss. Exits still go through. Failures throw Error starting with "Risk block:".',
        params: [],
      },
    ],
  },
  {
    group: 'Script contract',
    items: [
      {
        name: 'Tick / timeout',
        sig: 'async script body',
        desc: 'The whole file runs as an async function every Interval seconds (min 10s). Top-level await is allowed. Hard timeout 90s per tick (8s per bar in backtest). Return values are discarded unless you console.log them.',
        params: [],
      },
    ],
  },
];

function TemplateGuide({ template, dark, onLoad }) {
  if (!template) {
    return (
      <p className={`text-[11px] leading-relaxed ${dark ? 'text-slate-400' : 'text-gray-500'}`}>
        Select a strategy in Explorer or the Template dropdown. Its how-to, parameters, and risks show here.
      </p>
    );
  }
  const g = template.guide || {};
  return (
    <div className="space-y-3">
      <div>
        <div className="flex flex-wrap items-baseline gap-1.5">
          <h4 className={`text-[13px] font-semibold ${dark ? 'text-slate-100' : 'text-gray-900'}`}>{template.name}</h4>
          {template.category ? (
            <span className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
              dark ? 'bg-indigo-500/20 text-indigo-200' : 'bg-indigo-50 text-indigo-700'
            }`}>
              {template.category}
            </span>
          ) : null}
        </div>
        <p className={`text-[11px] mt-1 leading-relaxed ${dark ? 'text-slate-300' : 'text-gray-700'}`}>
          {g.summary || template.description}
        </p>
      </div>
      {g.howItWorks?.length > 0 && (
        <section>
          <div className={`text-[9px] font-semibold uppercase tracking-wider mb-1 ${dark ? 'text-slate-500' : 'text-gray-400'}`}>How it works</div>
          <ul className={`list-disc pl-3.5 space-y-1 text-[11px] leading-relaxed ${dark ? 'text-slate-300' : 'text-gray-700'}`}>
            {g.howItWorks.map((line, i) => <li key={i}>{line}</li>)}
          </ul>
        </section>
      )}
      {g.howToRun?.length > 0 && (
        <section>
          <div className={`text-[9px] font-semibold uppercase tracking-wider mb-1 ${dark ? 'text-slate-500' : 'text-gray-400'}`}>How to run</div>
          <ol className={`list-decimal pl-3.5 space-y-1 text-[11px] leading-relaxed ${dark ? 'text-slate-300' : 'text-gray-700'}`}>
            {g.howToRun.map((line, i) => <li key={i}>{line}</li>)}
          </ol>
        </section>
      )}
      {g.parameters?.length > 0 && (
        <section>
          <div className={`text-[9px] font-semibold uppercase tracking-wider mb-1 ${dark ? 'text-slate-500' : 'text-gray-400'}`}>Parameters</div>
          <ul className="space-y-1.5">
            {g.parameters.map((p, i) => (
              <li key={i} className="text-[11px] leading-relaxed">
                <code className={`px-1 rounded ${dark ? 'bg-white/10 text-indigo-200' : 'bg-gray-100 text-indigo-800'}`}>{p.name}</code>
                <span className={dark ? 'text-slate-400' : 'text-gray-600'}> — {p.meaning}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {g.risks?.length > 0 && (
        <section>
          <div className={`text-[9px] font-semibold uppercase tracking-wider mb-1 ${dark ? 'text-amber-400/80' : 'text-amber-700'}`}>Risks</div>
          <ul className={`list-disc pl-3.5 space-y-1 text-[11px] leading-relaxed ${dark ? 'text-amber-100/80' : 'text-amber-900/80'}`}>
            {g.risks.map((line, i) => <li key={i}>{line}</li>)}
          </ul>
        </section>
      )}
      {onLoad && (
        <button
          type="button"
          onClick={() => onLoad(template.id)}
          className={`text-[11px] font-medium ${dark ? 'text-indigo-300 hover:text-white' : 'text-indigo-600 hover:underline'}`}
        >
          Reload this template →
        </button>
      )}
    </div>
  );
}

function ApiEntry({ item, dark, open, onToggle }) {
  return (
    <article className={`rounded-md border ${dark ? 'border-white/10 bg-black/20' : 'border-gray-200 bg-white'}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={`w-full flex items-start gap-1.5 px-2 py-1.5 text-left rounded-md ${
          dark ? 'hover:bg-white/5' : 'hover:bg-gray-50'
        }`}
      >
        <FontAwesomeIcon
          icon={open ? faChevronDown : faChevronRight}
          className={`mt-0.5 text-[9px] shrink-0 ${dark ? 'text-slate-500' : 'text-gray-400'}`}
        />
        <div className="min-w-0 flex-1">
          <div className={`font-mono text-[11px] font-semibold break-all ${dark ? 'text-indigo-200' : 'text-indigo-800'}`}>
            {item.name}
          </div>
          {!open && item.sig ? (
            <div className={`font-mono text-[10px] mt-0.5 truncate ${dark ? 'text-slate-500' : 'text-gray-400'}`}>
              {item.sig}
            </div>
          ) : null}
        </div>
      </button>
      {open ? (
        <div className={`px-2 pb-2 pt-0.5 border-t ${dark ? 'border-white/5' : 'border-gray-100'}`}>
          {item.sig ? (
            <div className={`font-mono text-[10px] break-all ${dark ? 'text-slate-400' : 'text-gray-500'}`}>{item.sig}</div>
          ) : null}
          <p className={`text-[11px] mt-1.5 leading-relaxed ${dark ? 'text-slate-300' : 'text-gray-700'}`}>{item.desc}</p>
          {item.params?.length > 0 && (
            <ul className="mt-1.5 space-y-1">
              {item.params.map((p) => (
                <li key={p.name} className="text-[10px] leading-snug">
                  <code className={`px-0.5 rounded ${dark ? 'bg-white/10 text-teal-200' : 'bg-teal-50 text-teal-800'}`}>{p.name}</code>
                  {p.type ? <span className={dark ? 'text-slate-500' : 'text-gray-400'}> ({p.type})</span> : null}
                  <span className={dark ? 'text-slate-400' : 'text-gray-600'}> — {p.desc}</span>
                </li>
              ))}
            </ul>
          )}
          {item.returns ? (
            <p className={`text-[10px] mt-1 ${dark ? 'text-slate-500' : 'text-gray-500'}`}>
              Returns: {item.returns}
            </p>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

const ASSIST_MODEL_KEY = 'lt-algo-assist-model';

function AssistPanel({
  dark,
  script,
  symbols,
  onApplyScript,
}) {
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [turns, setTurns] = useState([]);
  const [draft, setDraft] = useState('');
  const [models, setModels] = useState([]);
  const [model, setModel] = useState(() => {
    try {
      return localStorage.getItem(ASSIST_MODEL_KEY) || '';
    } catch {
      return '';
    }
  });
  const [modelsError, setModelsError] = useState('');
  const endRef = useRef(null);

  const loadModels = async () => {
    setModelsError('');
    try {
      const response = await aiChatApi.listModels();
      const raw = response?.data;
      const list = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.models)
          ? raw.models
          : [];
      const names = list
        .filter((m) => {
          if (typeof m === 'string') return true;
          const caps = m?.capabilities;
          if (!Array.isArray(caps) || caps.length === 0) return true;
          return caps.includes('completion');
        })
        .map((m) => {
          if (typeof m === 'string') return { name: m, label: m };
          const name = m?.name;
          if (!name) return null;
          const size = m?.details?.parameter_size || '';
          return { name, label: size ? `${name} (${size})` : name };
        })
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name));
      setModels(names);
      setModel((prev) => {
        if (prev && names.some((m) => m.name === prev)) return prev;
        const envDefault =
          names.find((n) => n.name === 'llama3.2' || n.name.startsWith('llama3.2')) || names[0];
        const next = envDefault?.name || '';
        if (next) {
          try {
            localStorage.setItem(ASSIST_MODEL_KEY, next);
          } catch {
            /* ignore */
          }
        }
        return next;
      });
    } catch (err) {
      setModelsError(err.response?.data?.error || err.message || 'Could not list Ollama models');
      setModels([]);
    }
  };

  useEffect(() => {
    loadModels();
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns, busy, draft]);

  const generate = async () => {
    const q = prompt.trim();
    if (!q || busy) return;
    setBusy(true);
    setError('');
    setTurns((prev) => [
      ...prev,
      { role: 'user', content: q },
      { role: 'assistant', content: '', script: '', model, streaming: true },
    ]);
    setPrompt('');
    try {
      const history = turns.slice(-6).map((t) => ({
        role: t.role,
        content: t.role === 'assistant' ? (t.script || t.content) : t.content,
      }));
      const res = await aiChatApi.generateAlgo({
        prompt: q,
        script,
        symbols,
        model: model || undefined,
        history,
        onDelta: (text) => {
          setTurns((prev) => {
            const copy = [...prev];
            const last = copy[copy.length - 1];
            if (!last?.streaming) return prev;
            copy[copy.length - 1] = { ...last, content: `${last.content || ''}${text}` };
            return copy;
          });
        },
      });
      const data = res.data || {};
      setTurns((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = {
          role: 'assistant',
          content: data.notes || copy[copy.length - 1]?.content || 'Generated a script.',
          script: data.script || '',
          model: data.model || model,
        };
        return copy;
      });
      setDraft(data.script || '');
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Ollama request failed';
      setError(msg);
      setTurns((prev) => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last?.streaming) {
          copy[copy.length - 1] = { role: 'assistant', content: `Error: ${msg}`, script: '' };
          return copy;
        }
        return [...prev, { role: 'assistant', content: `Error: ${msg}`, script: '' }];
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-0">
        {turns.length === 0 && (
          <div className={`text-[11px] leading-relaxed ${dark ? 'text-slate-400' : 'text-gray-500'}`}>
            Describe a strategy. The local Ollama model sees the workbench APIs, current editor, and Symbols field, then writes a runnable script.
            <div className={`mt-2 space-y-1 ${dark ? 'text-slate-500' : 'text-gray-400'}`}>
              <div>“Golden cross on daily bars with 1% risk and a 3/1.5 bracket.”</div>
              <div>“RSI 14 mean-reversion, buy &lt; 30, flatten &gt; 70.”</div>
              <div>“Add a trailing stop to the current script.”</div>
            </div>
          </div>
        )}
        {turns.map((t, i) => (
          <div
            key={`${t.role}-${i}`}
            className={`rounded-md px-2 py-1.5 text-[11px] leading-relaxed ${
              t.role === 'user'
                ? dark
                  ? 'bg-indigo-500/15 text-indigo-100'
                  : 'bg-indigo-50 text-indigo-900'
                : dark
                  ? 'bg-black/25 text-slate-200 border border-white/10'
                  : 'bg-white text-gray-800 border border-gray-200'
            }`}
          >
            <div className={`text-[9px] font-semibold uppercase tracking-wider mb-0.5 ${
              dark ? 'text-slate-500' : 'text-gray-400'
            }`}>
              {t.role === 'user' ? 'You' : t.model || 'Ollama'}
            </div>
            {t.content ? <p className="whitespace-pre-wrap">{t.content}</p> : null}
            {t.script ? (
              <pre className={`mt-1.5 max-h-48 overflow-auto text-[10px] font-mono p-1.5 rounded ${
                dark ? 'bg-black/40 text-slate-300' : 'bg-gray-50 text-gray-700'
              }`}>
                {t.script}
              </pre>
            ) : null}
            {t.script && onApplyScript ? (
              <button
                type="button"
                onClick={() => {
                  onApplyScript(t.script);
                  setDraft(t.script);
                }}
                className={`mt-1.5 text-[10px] font-semibold ${
                  dark ? 'text-indigo-300 hover:text-white' : 'text-indigo-600 hover:underline'
                }`}
              >
                Apply to editor
              </button>
            ) : null}
          </div>
        ))}
        {busy && (
          <div className={`text-[11px] ${dark ? 'text-slate-400' : 'text-gray-500'}`}>
            <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />
            Writing with {model || 'Ollama'}… tokens appear as they stream. Large models can take several minutes.
          </div>
        )}
        {error ? (
          <div className={`text-[11px] ${dark ? 'text-red-300' : 'text-red-700'}`}>{error}</div>
        ) : null}
        <div ref={endRef} />
      </div>
      <div className={`shrink-0 p-2 border-t space-y-1.5 ${dark ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="flex items-center gap-1">
          <select
            value={model}
            onChange={(e) => {
              const next = e.target.value;
              setModel(next);
              try {
                localStorage.setItem(ASSIST_MODEL_KEY, next);
              } catch {
                /* ignore */
              }
            }}
            disabled={busy || models.length === 0}
            title="Local Ollama model"
            className={`flex-1 min-w-0 h-7 px-1 rounded text-[10px] ${
              dark
                ? 'bg-black/30 border border-white/10 text-slate-200'
                : 'bg-white border border-gray-200 text-gray-800'
            }`}
          >
            {models.length === 0 ? (
              <option value="">{modelsError ? 'Ollama unavailable' : 'Loading models…'}</option>
            ) : (
              models.map((m) => (
                <option key={m.name} value={m.name}>
                  {m.label}
                </option>
              ))
            )}
          </select>
          <button
            type="button"
            onClick={loadModels}
            disabled={busy}
            title="Refresh model list from Ollama"
            className={`h-7 px-2 rounded text-[10px] font-medium shrink-0 ${
              dark ? 'text-slate-300 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-200'
            }`}
          >
            Refresh
          </button>
        </div>
        {modelsError ? (
          <p className={`text-[10px] ${dark ? 'text-amber-300' : 'text-amber-700'}`}>{modelsError}</p>
        ) : null}
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              generate();
            }
          }}
          rows={3}
          placeholder="What should this algorithm do?"
          disabled={busy}
          className={`w-full resize-none rounded px-2 py-1.5 text-[11px] leading-relaxed focus:outline-none ${
            dark
              ? 'bg-black/30 border border-white/10 text-slate-100 placeholder:text-slate-500'
              : 'bg-white border border-gray-200 text-gray-800 placeholder:text-gray-400'
          }`}
        />
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={generate}
            disabled={busy || !prompt.trim() || !model}
            className="h-7 px-2.5 rounded bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white text-[11px] font-semibold inline-flex items-center gap-1"
          >
            <FontAwesomeIcon icon={busy ? faSpinner : faRobot} className={`text-[10px] ${busy ? 'animate-spin' : ''}`} />
            Generate
          </button>
          {draft && onApplyScript ? (
            <button
              type="button"
              onClick={() => onApplyScript(draft)}
              className={`h-7 px-2 rounded text-[11px] font-medium ${
                dark ? 'text-indigo-200 hover:bg-white/10' : 'text-indigo-700 hover:bg-indigo-50'
              }`}
            >
              Apply last
            </button>
          ) : null}
          {turns.length > 0 ? (
            <button
              type="button"
              onClick={() => {
                setTurns([]);
                setDraft('');
                setError('');
              }}
              className={`ml-auto h-7 w-7 inline-flex items-center justify-center rounded ${
                dark ? 'text-slate-500 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:bg-gray-200'
              }`}
              title="Clear chat"
            >
              <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Collapsible workbench docs: selected strategy guide + API catalog + Ollama coder.
 */
export default function AlgoWorkbenchDocs({
  open,
  onToggle,
  dark = false,
  template = null,
  templates = [],
  onLoadTemplate,
  activeTab,
  onActiveTab,
  script = '',
  symbols = '',
  onApplyScript,
}) {
  const [tab, setTab] = useState(activeTab || 'guide');
  const [query, setQuery] = useState('');
  const [guidePick, setGuidePick] = useState('');
  const [expandedApis, setExpandedApis] = useState(() => new Set());

  useEffect(() => {
    if (activeTab) setTab(activeTab);
  }, [activeTab]);

  const selectTab = (id) => {
    setTab(id);
    onActiveTab?.(id);
  };

  const shownTemplate = useMemo(() => {
    if (guidePick) return templates.find((t) => t.id === guidePick) || template;
    return template;
  }, [guidePick, templates, template]);

  const filteredCatalog = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ALGO_API_CATALOG;
    return ALGO_API_CATALOG.map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        const blob = [
          item.name,
          item.sig,
          item.desc,
          ...(item.params || []).map((p) => `${p.name} ${p.desc}`),
        ]
          .join(' ')
          .toLowerCase();
        return blob.includes(q);
      }),
    })).filter((g) => g.items.length);
  }, [query]);

  const visibleApiNames = useMemo(
    () => filteredCatalog.flatMap((g) => g.items.map((item) => item.name)),
    [filteredCatalog],
  );

  const toggleApi = (name) => {
    setExpandedApis((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const expandAllApis = () => {
    setExpandedApis(new Set(visibleApiNames));
  };

  const collapseAllApis = () => {
    setExpandedApis(new Set());
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={onToggle}
        title="Show strategy docs & API"
        className={`flex w-full h-8 lg:w-8 lg:h-full shrink-0 flex-row lg:flex-col items-center justify-center lg:justify-start pt-0 lg:pt-2 gap-2 border-t lg:border-t-0 lg:border-l ${
          dark
            ? 'border-white/10 bg-[#252526] text-slate-400 hover:text-white hover:bg-white/5'
            : 'border-gray-200 bg-gray-50 text-gray-500 hover:text-gray-800 hover:bg-gray-100'
        }`}
      >
        <FontAwesomeIcon icon={faChevronLeft} className="text-[10px] hidden lg:inline" />
        <FontAwesomeIcon icon={faBook} className="text-[11px]" />
        <span className="text-[10px] font-semibold uppercase tracking-widest lg:hidden">Docs</span>
        <span
          className="hidden lg:inline text-[9px] font-semibold uppercase tracking-widest"
          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
        >
          Docs
        </span>
      </button>
    );
  }

  return (
    <aside
      className={`flex w-full lg:w-80 xl:w-96 shrink-0 flex-col border-t lg:border-t-0 lg:border-l min-h-0 max-h-[42vh] lg:max-h-none lg:h-full ${
        dark ? 'border-white/10 bg-[#252526]' : 'border-gray-200 bg-gray-50'
      }`}
    >
      <div className={`h-9 px-2 flex items-center gap-1 border-b shrink-0 ${dark ? 'border-white/10' : 'border-gray-200'}`}>
        <FontAwesomeIcon icon={faBook} className={`text-[10px] ${dark ? 'text-indigo-300' : 'text-indigo-600'}`} />
        <span className={`text-[10px] font-semibold uppercase tracking-wider ${dark ? 'text-slate-400' : 'text-gray-500'}`}>
          Docs
        </span>
        <div className="ml-auto flex items-center gap-0.5">
          {[
            { id: 'guide', label: 'Strategy' },
            { id: 'api', label: 'API' },
            { id: 'assist', label: 'Assist' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => selectTab(t.id)}
              className={`h-6 px-1.5 rounded text-[10px] font-semibold ${
                tab === t.id
                  ? dark
                    ? 'bg-white/10 text-white'
                    : 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200'
                  : dark
                    ? 'text-slate-400 hover:text-slate-200'
                    : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {t.label}
            </button>
          ))}
          <button
            type="button"
            onClick={onToggle}
            title="Hide docs"
            className={`h-6 w-6 inline-flex items-center justify-center rounded ${
              dark ? 'text-slate-400 hover:bg-white/10 hover:text-white' : 'text-gray-500 hover:bg-gray-200'
            }`}
          >
            <FontAwesomeIcon icon={faChevronRight} className="text-[10px]" />
          </button>
        </div>
      </div>

      {tab === 'guide' && (
        <div className="px-2 py-1.5 border-b shrink-0">
          <select
            value={guidePick || template?.id || ''}
            onChange={(e) => setGuidePick(e.target.value)}
            className={`w-full h-7 px-1.5 rounded text-[11px] ${
              dark
                ? 'bg-black/30 border border-white/10 text-slate-200'
                : 'bg-white border border-gray-200 text-gray-800'
            }`}
          >
            <option value="">Current template…</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {tab === 'api' && (
        <div className={`px-2 py-1.5 border-b shrink-0 space-y-1.5 ${dark ? 'border-white/10' : 'border-gray-200'}`}>
          <div className={`flex items-center gap-1.5 h-7 px-2 rounded ${
            dark ? 'bg-black/30 border border-white/10' : 'bg-white border border-gray-200'
          }`}>
            <FontAwesomeIcon icon={faSearch} className={`text-[9px] ${dark ? 'text-slate-500' : 'text-gray-400'}`} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter APIs…"
              className={`flex-1 bg-transparent text-[11px] focus:outline-none ${
                dark ? 'text-slate-200 placeholder:text-slate-500' : 'text-gray-800 placeholder:text-gray-400'
              }`}
            />
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={expandAllApis}
              className={`h-6 px-1.5 rounded text-[10px] font-semibold ${
                dark
                  ? 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
                  : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              Expand all
            </button>
            <button
              type="button"
              onClick={collapseAllApis}
              className={`h-6 px-1.5 rounded text-[10px] font-semibold ${
                dark
                  ? 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
                  : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              Collapse all
            </button>
          </div>
        </div>
      )}

      {tab === 'assist' ? (
        <AssistPanel
          dark={dark}
          script={script}
          symbols={symbols}
          onApplyScript={onApplyScript}
        />
      ) : (
      <div className="flex-1 overflow-y-auto p-2 space-y-3 min-h-0">
        {tab === 'guide' ? (
          <TemplateGuide
            template={shownTemplate}
            dark={dark}
            onLoad={onLoadTemplate}
          />
        ) : (
          <>
            <p className={`text-[10px] leading-relaxed ${dark ? 'text-slate-500' : 'text-gray-500'}`}>
              These run in your script as <code className="px-0.5">alpaca</code>, <code className="px-0.5">helpers</code>, and injected <code className="px-0.5">symbol</code> / <code className="px-0.5">symbols</code>. Log results with console.log.
            </p>
            {filteredCatalog.length === 0 ? (
              <p className={`text-[11px] ${dark ? 'text-slate-500' : 'text-gray-400'}`}>No matches.</p>
            ) : (
              filteredCatalog.map((group) => (
                <section key={group.group} className="space-y-1.5">
                  <div className={`text-[9px] font-semibold uppercase tracking-wider sticky top-0 py-0.5 ${
                    dark ? 'text-slate-500 bg-[#252526]' : 'text-gray-400 bg-gray-50'
                  }`}>
                    {group.group}
                  </div>
                  {group.items.map((item) => (
                    <ApiEntry
                      key={item.name}
                      item={item}
                      dark={dark}
                      open={expandedApis.has(item.name)}
                      onToggle={() => toggleApi(item.name)}
                    />
                  ))}
                </section>
              ))
            )}
          </>
        )}
      </div>
      )}
    </aside>
  );
}
